import { Link } from "react-router-dom";
import { Crown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Soft-locks children behind a Premium subscription.
 *
 *  - `mode="overlay"` (default): renders children dimmed behind a centered
 *    upgrade card. Use for full sections / pages.
 *  - `mode="inline"`: renders a small inline "Premium" pill + upgrade link
 *    in place of children. Use for buttons / form rows.
 */
export default function PremiumGate({
  children,
  feature = "this feature",
  mode = "overlay",
  className = "",
}) {
  const { user } = useAuth();
  if (user?.is_premium) return children;

  if (mode === "inline") {
    return (
      <div
        className={`rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-3 flex items-center gap-3 ${className}`}
        data-testid="premium-gate-inline"
      >
        <Crown strokeWidth={1.5} className="size-4 text-primary shrink-0" />
        <div className="flex-1 text-xs">
          <span className="font-semibold">Premium</span> · {feature}
        </div>
        <Button
          size="sm"
          asChild
          className="rounded-lg h-8 px-3 text-xs"
          data-testid="premium-gate-upgrade-inline"
        >
          <Link to="/upgrade">Upgrade</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} data-testid="premium-gate-overlay">
      <div className="pointer-events-none opacity-30 blur-[1px] select-none" aria-hidden>
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <div className="rounded-2xl border-2 border-primary/40 bg-card/95 backdrop-blur p-6 max-w-md text-center shadow-lg">
          <div className="size-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
            <Sparkles strokeWidth={1.5} className="size-6 text-primary" />
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
            Lumivera Premium
          </div>
          <h3 className="font-display text-2xl font-extrabold tracking-tight mt-1">
            Unlock {feature}
          </h3>
          <p className="text-sm text-muted-foreground mt-2">
            Start a 14-day free trial — no card required to start.
          </p>
          <Button asChild className="rounded-xl mt-4 w-full" data-testid="premium-gate-upgrade">
            <Link to="/upgrade">
              <Crown strokeWidth={1.5} className="size-4 mr-1.5" />
              See Premium
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
